import { useEffect, useState } from 'react'
import { Bell, Mail } from 'lucide-react'
import Button from '../../components/Button'
import { FormBody, FormCols, Textarea } from '../../components/Form'
import DatePicker from '../../components/DatePicker'
import FormRow from '../../components/FormRow'
import Modal from '../../components/Modal'
import TextField from '../../components/TextField'
import { strings as S } from './strings'
import './modals.css'

const EMPTY = { title: '', sendTime: '', body: '' }

/**
 * `#sendEmail_modal` — schedules a message to every member of one group.
 *
 * The legacy body is a CKEditor instance, but the submit handler harvests it with
 * `$("div.ck-editor__main p").text()` — plain text is what actually reaches the
 * server, so a textarea carries the same payload without pulling in an editor.
 *
 * Validation is sequential and one message at a time, as in js.cshtml:645-693.
 */
export default function GroupEmailModal({ open, group, onClose, onSubmit, onShowBatches }) {
  const [values, setValues] = useState(EMPTY)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setValues(EMPTY)
    setError('')
  }, [open, group])

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  const submit = (event) => {
    event.preventDefault()
    if (!values.title.trim()) return setError(S.emailModal.missingTitle)
    if (!values.sendTime.trim()) return setError(S.emailModal.missingSendTime)
    if (!values.body.trim()) return setError(S.emailModal.missingBody)
    setError('')
    return onSubmit({ groupId: group?.id, ...values })
  }

  return (
    <Modal
      open={open}
      size="lg"
      title={S.emailModal.title}
      icon={Mail}
      subtitle={group?.name}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onShowBatches}>
            <Bell size={16} strokeWidth={1.75} />
            {S.emailModal.previousNotifications}
          </Button>
          <Button type="submit" form="group-email-form">
            {S.emailModal.submit}
          </Button>
        </>
      }
    >
      <form id="group-email-form" onSubmit={submit} noValidate>
        <FormBody>
          <FormCols>
            <FormRow label={S.emailModal.messageTitle}>
              {(id) => <TextField id={id} value={values.title} onChange={set('title')} />}
            </FormRow>

            <FormRow label={S.emailModal.sendDate}>
              {(id) => (
                <DatePicker id={id} value={values.sendTime} onChange={set('sendTime')} />
              )}
            </FormRow>
          </FormCols>

          <FormRow label={S.emailModal.messageBody}>
            {(id) => <Textarea id={id} rows={8} value={values.body} onChange={set('body')} />}
          </FormRow>

          {error && (
            <p className="form-row__error" role="alert">
              <bdi>{error}</bdi>
            </p>
          )}
        </FormBody>
      </form>
    </Modal>
  )
}
